import { useState } from "react";
import { useTranslation } from "react-i18next";

export interface NewParkingSpotDraft {
    floor: string;
    numberRaw: string;
    holderName: string;
    holderEmail: string;
    maxAssignments: string;
    notes: string;
}

interface Props {
    floors: string[];
    draft: NewParkingSpotDraft;
    onChange: (patch: Partial<NewParkingSpotDraft>) => void;
}

export function NewParkingSpot({ floors, draft, onChange }: Props) {
    const { t } = useTranslation();
    const [customFloor, setCustomFloor] = useState(false);

    const floorOptions = floors.length > 0 ? floors : ["1", "2"];
    const isCustom = customFloor || !floorOptions.includes(draft.floor);

    return (
        <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
            <div className="form-group">
                <label className="font-weight-bold mb-1">
                    {t("mod_parking_floor")}
                </label>
                {isCustom ? (
                    <div className="d-flex align-items-center">
                        <input
                            type="text"
                            name="parking-new-floor"
                            autoComplete="off"
                            className="form-control form-control-sm"
                            value={draft.floor}
                            onChange={(e) => onChange({ floor: e.target.value })}
                        />
                        <button
                            type="button"
                            className="btn btn-sm btn-link ml-2"
                            onClick={() => setCustomFloor(false)}
                        >
                            {t("global_cancel")}
                        </button>
                    </div>
                ) : (
                    <div className="d-flex flex-wrap align-items-center">
                        {floorOptions.map((f) => (
                            <label
                                key={f}
                                className="mb-0 mr-3 d-flex align-items-center"
                            >
                                <input
                                    type="radio"
                                    name="parking-new-floor"
                                    value={f}
                                    checked={draft.floor === f}
                                    onChange={() => onChange({ floor: f })}
                                    className="mr-1"
                                />
                                {t("mod_parking_floor")} {f}
                            </label>
                        ))}
                        <button
                            type="button"
                            className="btn btn-sm btn-link"
                            onClick={() => {
                                setCustomFloor(true);
                                onChange({ floor: "" });
                            }}
                        >
                            {t("mod_parking_new_other_floor")}
                        </button>
                    </div>
                )}
            </div>

            <div className="form-group">
                <label className="font-weight-bold mb-1">
                    {t("mod_parking_number")}
                </label>
                <input
                    type="text"
                    name="parking-new-number"
                    autoComplete="off"
                    inputMode="numeric"
                    className="form-control form-control-sm"
                    value={draft.numberRaw}
                    onChange={(e) => onChange({ numberRaw: e.target.value })}
                />
            </div>

            <div className="form-group">
                <label className="font-weight-bold mb-1">
                    {t("mod_parking_holder")}{" "}
                    <small className="text-muted">
                        ({t("global_optional")})
                    </small>
                </label>
                <input
                    type="text"
                    name="parking-new-holder-name"
                    autoComplete="off"
                    className="form-control form-control-sm mb-2"
                    value={draft.holderName}
                    onChange={(e) => onChange({ holderName: e.target.value })}
                    placeholder={t("mod_parking_new_holder_name_placeholder")}
                />
                <input
                    type="email"
                    name="parking-new-holder-email"
                    autoComplete="off"
                    className="form-control form-control-sm"
                    value={draft.holderEmail}
                    onChange={(e) => onChange({ holderEmail: e.target.value })}
                    placeholder={t("mod_parking_new_holder_email_placeholder")}
                />
            </div>

            <div className="form-group">
                <label className="font-weight-bold mb-1">
                    {t("mod_parking_max_assignments")}
                </label>
                <input
                    type="number"
                    min={0}
                    name="parking-new-max"
                    autoComplete="off"
                    className="form-control form-control-sm"
                    value={draft.maxAssignments}
                    onChange={(e) =>
                        onChange({ maxAssignments: e.target.value })
                    }
                />
                <small className="text-muted">
                    {t("mod_parking_max_assignments_help")}
                </small>
            </div>

            <div className="form-group mb-0">
                <label className="font-weight-bold mb-1">
                    {t("mod_parking_notes")}{" "}
                    <small className="text-muted">
                        ({t("global_optional")})
                    </small>
                </label>
                <textarea
                    name="parking-new-notes"
                    autoComplete="off"
                    className="form-control form-control-sm"
                    rows={2}
                    value={draft.notes}
                    onChange={(e) => onChange({ notes: e.target.value })}
                />
            </div>
        </form>
    );
}
